import type { Account, AccountModule, UserSettings } from '@/api/auth';
import { formatAmount } from './player-balance';

/**
 * Ribbon flags on a casino game tile. The API sends these as strings, matching the
 * legacy `Enums.CasinoFlagTypes`.
 */
export const CasinoFlag = {
  New: '0',
  Jackpot: '1',
  DropsAndWins: '2',
  MostPlayed: '3',
  BonusBuy: '4',
  HotGames: '5',
  Megaways: '6',
  InstantWin: '7',
  Popular: '8',
} as const;

export type CasinoFlag = (typeof CasinoFlag)[keyof typeof CasinoFlag];

export const CASINO_FLAG_LABEL: Record<CasinoFlag, string> = {
  [CasinoFlag.New]: 'New',
  [CasinoFlag.Jackpot]: 'Jackpot',
  [CasinoFlag.DropsAndWins]: 'Drops & wins',
  [CasinoFlag.MostPlayed]: 'Most played',
  [CasinoFlag.BonusBuy]: 'Bonus buy',
  [CasinoFlag.HotGames]: 'Hot',
  [CasinoFlag.Megaways]: 'Megaways',
  [CasinoFlag.InstantWin]: 'Instant win',
  [CasinoFlag.Popular]: 'Popular',
};

/** URL menu segment → `FlagType` (legacy `SetAdditionalFilters`). */
export const CASINO_FLAG_SLUGS: Record<string, CasinoFlag> = {
  newgames: CasinoFlag.New,
  dropsandwins: CasinoFlag.DropsAndWins,
  bonusbuy: CasinoFlag.BonusBuy,
  megaways: CasinoFlag.Megaways,
  hotgames: CasinoFlag.HotGames,
};

/** Providers that serve game thumbs as `.jpg` instead of `.png`. */
const JPG_THUMB_PROVIDERS = new Set(['amatic', 'riseup', 'matgaming', 'matgaminglc', 'mtmgaming']);

export interface CasinoGame {
  Id?: number | null;
  VendorId?: number | null;
  VendorName: string;
  CategoryId?: number | null;
  CategoryName: string;
  ExternalId?: string | null;
  Name: string;
  GameTag: string;
  GameCategoryTag?: string | null;
  ProviderId?: number | null;
  ProviderName: string;
  Popular?: boolean;
  FavoriteId?: number | null;
  ProviderGameId: number;
  Position?: number | null;
}

export interface CasinoVendor {
  Id: number;
  Name: string;
  ProviderId?: number | null;
  ProviderName?: string | null;
  Position?: number | null;
}

export interface CasinoCategory {
  CategoryId: number;
  CategoryName: string;
}

export interface CasinoPaging {
  PageNumber: number;
  NumberOfResultsToDisplay?: number;
  NumberOfPages?: number;
  NumberOfResults?: number;
  TotalNumberOfResults?: number;
}

export interface CasinoGamesPage {
  Data: CasinoGame[];
  Filter?: CasinoPaging | null;
}

export interface CasinoGameFilters {
  AccountGuid: string;
  MachineGuid?: string | null;
  ProviderId?: number | null;
  Vendor?: string | null;
  Category?: string | null;
  Popular?: boolean | null;
  Mobile?: boolean | null;
  Casino?: boolean | null;
  LiveCasino?: boolean | null;
  VirtualSports?: boolean | null;
  CurrencyId?: number;
  CountryId?: number | null;
  FavoriteGames?: boolean | null;
  FlagType?: number | string | null;
  SearchText?: string | null;
}

export interface CasinoListOptions {
  Paging: CasinoPaging;
  Sorting?: { SortBy?: string; Direction?: string };
  TableFilters: CasinoGameFilters;
}

export interface GameSessionRequest {
  WalletId: string;
  Game: string;
  Vendor: string;
  Language?: string;
  Lang?: string;
  ReturnUrl?: string;
  Exit?: string;
  IsBonus?: boolean;
  Mobile?: boolean | string;
  /** Legacy: `desktop` | `mobile` (slotsapi-style) or `WEB` | `MOBILE` (pragmatic). */
  Device?: string;
  AdminId?: string | null;
  Currency?: string | null;
  /** Legacy `SId-{user.SId}` for slotsapi / kosmos / cgames / ingames, etc. */
  Account?: string | null;
  /** `window.location.host` — no protocol. */
  WebSiteUrl?: string | null;
  /** Legacy hard-coded casino share, usually `'90'`. */
  Percent?: string | null;
  ManagerId?: string | number | null;
  [key: string]: unknown;
}

export interface GameSessionResponse {
  Success?: boolean;
  Error?: string | null;
  GameUrl?: string | null;
  Token?: string | null;
  AgentId?: string | null;
  BonusIsAvailable?: boolean;
  [key: string]: unknown;
}

/** Raw DTO from trsAPI `latestcasinotransactions/list`. */
export interface LatestCasinoTransaction {
  ProviderId: number;
  VendorId: number;
  GameTag: string;
  Game: string;
  Amount: number;
  Currency: string;
  Timestamp: string;
}

/** Win after legacy fake-multiplier assignment for the Latest Wins ticker. */
export interface EnrichedLatestCasinoWin extends LatestCasinoTransaction {
  multiplierLabel: string;
  hot: boolean;
}

/** Raw DTO from trsAPI `biggestwinnings`. */
export interface BiggestCasinoWinning {
  ProviderId: number;
  VendorId: number;
  GameTag: string;
  Game: string;
  Amount: number;
  Currency: string;
  Timestamp: string;
}

/** Win after vendor lookup + ranking (legacy `CreateBiggestWinsElement`). */
export interface EnrichedBiggestCasinoWinning extends BiggestCasinoWinning {
  ProviderName: string;
  VendorName: string;
  Ranking: number;
}

function accountModulesList(
  account: Account | null | undefined,
  user: UserSettings | null | undefined,
): AccountModule[] {
  const enabled = account?.EnabledModules;
  if (Array.isArray(enabled) && enabled.length > 0) return enabled;
  const userModules = user?.Modules;
  if (Array.isArray(userModules) && userModules.length > 0) return userModules;
  const accountModules = account?.Modules;
  if (Array.isArray(accountModules) && accountModules.length > 0) return accountModules;
  return [];
}

/** Legacy homepage / lobby gate: account module `BiggestCasinoWins`. */
export function isBiggestCasinoWinsEnabled(
  account: Account | null | undefined,
  user?: UserSettings | null,
): boolean {
  return accountModulesList(account, user).some(
    (mod) => typeof mod?.Name === 'string' && mod.Name.toLowerCase() === 'biggestcasinowins',
  );
}

/** Legacy homepage / lobby gate: account module `LatestRoundsAndBets`. */
export function isLatestRoundsAndBetsEnabled(
  account: Account | null | undefined,
  user?: UserSettings | null,
): boolean {
  return accountModulesList(account, user).some(
    (mod) => typeof mod?.Name === 'string' && mod.Name.toLowerCase() === 'latestroundsandbets',
  );
}

/**
 * Legacy `LatestCasinoTransactions` fake multiplier from amount bands.
 * Optional `random` (0..1) makes banding/hot deterministic in tests.
 */
export function computeLatestWinMultiplier(
  amount: number,
  random: () => number = Math.random,
): { multiplierLabel: string; hot: boolean } {
  if (!Number.isFinite(amount) || amount <= 0) {
    return { multiplierLabel: '0.00x', hot: false };
  }
  const absoluteAmount = Math.abs(amount);
  let normalMaxMultiplier = 8;
  if (absoluteAmount < 0.1) normalMaxMultiplier = 1.2;
  else if (absoluteAmount < 1) normalMaxMultiplier = 2;
  else if (absoluteAmount < 10) normalMaxMultiplier = 3.5;
  else if (absoluteAmount < 50) normalMaxMultiplier = 5;

  let multiplierValue = 1 + random() * (normalMaxMultiplier - 1);
  let hot = false;
  if (absoluteAmount >= 20 && random() < 0.2) {
    multiplierValue = Math.max(multiplierValue * 100, 100 + random() * 200);
    hot = true;
  }
  return { multiplierLabel: `${multiplierValue.toFixed(2)}x`, hot };
}

/** Assign fake multipliers once per fetch (stable for the ticker lifetime). */
export function assignLatestWinMultipliers(
  wins: LatestCasinoTransaction[],
  random: () => number = Math.random,
): EnrichedLatestCasinoWin[] {
  return wins.map((win) => {
    const { multiplierLabel, hot } = computeLatestWinMultiplier(win.Amount, random);
    return { ...win, multiplierLabel, hot };
  });
}

/**
 * Match wins to vendors by VendorId + ProviderId; skip unmatched; assign 1-based Ranking.
 */
export function enrichBiggestCasinoWins(
  wins: BiggestCasinoWinning[],
  vendors: CasinoVendor[],
): EnrichedBiggestCasinoWinning[] {
  const result: EnrichedBiggestCasinoWinning[] = [];
  let ranking = 1;
  for (const win of wins) {
    const vendor = vendors.find((v) => v.Id === win.VendorId && v.ProviderId === win.ProviderId);
    if (!vendor) continue;
    result.push({
      ...win,
      VendorName: vendor.Name,
      ProviderName: (vendor.ProviderName ?? '').trim() || vendor.Name,
      Ranking: ranking++,
    });
  }
  return result;
}

/** Figma timestamp: `DD/MM/YY - HH:mm`. */
export function formatBiggestWinTimestamp(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} - ${hh}:${min}`;
}

/** Figma amount: currency prefix + `formatAmount` (e.g. `₺1,083,992.00`). */
export function formatBiggestWinAmount(amount: number, currency: string | null | undefined): string {
  const formatted = formatAmount(amount, 2);
  const code = (currency ?? '').trim();
  return code ? `${code}${formatted}` : formatted;
}

/** Same Figma amount format as biggest wins. */
export function formatLatestWinAmount(amount: number, currency: string | null | undefined): string {
  return formatBiggestWinAmount(amount, currency);
}

/** Play path for a biggest-win tile (legacy hardcodes category `slots`). */
export function biggestWinPlayPath(win: EnrichedBiggestCasinoWinning): string {
  return casinoPlayPath(
    {
      Name: win.Game,
      GameTag: win.GameTag,
      VendorName: win.VendorName,
      ProviderName: win.ProviderName,
      CategoryName: 'slots',
      ProviderGameId: 0,
    },
    'slots',
  );
}

/** Normalize provider / vendor / gameTag for URL and asset paths. */
export function casinoSlug(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/\s+/g, '').replace(/\W/g, '');
}

/** Display slug used in navigation (spaces removed, lowercased). */
export function casinoNavSlug(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/\s+/g, '');
}

/**
 * Game thumbnail under `/gapp/{provider}/images/{vendor}/{tag}.{ext}`.
 * Base is currently the brand origin (`VITE_BRAND_ORIGIN` / contra11.com).
 */
export function casinoGameThumbUrl(
  casinoBaseUrl: string,
  game: Pick<CasinoGame, 'ProviderName' | 'VendorName' | 'GameTag'>,
): string {
  const provider = casinoNavSlug(game.ProviderName);
  const vendor = casinoNavSlug(game.VendorName);
  const tag = (game.GameTag ?? '').toLowerCase().replace(/\s+/g, '');
  const ext = JPG_THUMB_PROVIDERS.has(provider) ? 'jpg' : 'png';
  const base = casinoBaseUrl.replace(/\/+$/, '');
  return `${base}/gapp/${provider}/images/${vendor}/${tag}.${ext}`;
}

/**
 * Vendor logo under `/assets/images/vendors/svg/{slug}.svg`.
 * Base is currently the brand origin (`VITE_BRAND_ORIGIN` / contra11.com).
 */
export function casinoVendorLogoUrl(casinoBaseUrl: string, vendorName: string): string {
  const base = casinoBaseUrl.replace(/\/+$/, '');
  return `${base}/assets/images/vendors/svg/${casinoSlug(vendorName)}.svg`;
}

/** Deep-link path to open a game (legacy navigate shape without display name). */
export function casinoPlayPath(game: CasinoGame, categoryOverride?: string): string {
  const category = casinoNavSlug(categoryOverride ?? game.CategoryName ?? game.GameCategoryTag ?? 'slots');
  const provider = casinoNavSlug(game.ProviderName);
  const vendor = casinoNavSlug(game.VendorName);
  const tag = encodeURIComponent(game.GameTag);
  const name = encodeURIComponent(game.Name);
  return `/casino/${category}/${provider}/${vendor}/${tag}/${name}`;
}

export function casinoCategoryPath(category: string, vendorOrFlag?: string): string {
  const cat = casinoNavSlug(category) || 'slots';
  if (!vendorOrFlag) return `/casino/${cat}`;
  return `/casino/${cat}/${casinoNavSlug(vendorOrFlag)}`;
}

export function isCasinoFlagSlug(segment: string | undefined): boolean {
  if (!segment) return false;
  const key = casinoNavSlug(segment);
  return key === 'popular' || key in CASINO_FLAG_SLUGS;
}

export function flagTypeFromSlug(segment: string | undefined): CasinoFlag | null {
  if (!segment) return null;
  return CASINO_FLAG_SLUGS[casinoNavSlug(segment)] ?? null;
}

/**
 * Map navigation provider slug → WalletAPI session path segment
 * (legacy `OpenProviderGame` switch).
 */
export const PROVIDER_SESSION_SLUG: Record<string, string> = {
  amatic: 'gapi',
  smartsoftgaming: 'ssgaming',
  pragmaticplay: 'pragmatic',
  pragmaticplaylc: 'pragmatic',
  gga: 'gga',
  ggalc: 'gga',
  matgaming: 'matgaming',
  matgaminglc: 'matgaming',
  mtmgaming: 'matgaming',
  spribe: 'spribe',
  bsk: 'bsk',
  hacksaw: 'hacksaw',
  gamingconstruct: 'gamingconstruct',
  megargs: 'megargs',
  contraslot: 'contraslot',
  slotsapi: 'slotsapi',
  slotsapi2: 'slotsapi2',
  apigames: 'apigames',
  xlive: 'xlive',
  cgames: 'cgames',
  kosmos: 'kosmos',
  gamesservice: 'gamesservice',
  ingames: 'ingames',
  altira: 'altira',
  riseup: 'riseup',
  ncasino: 'ncasino',
  mcasino: 'mcasino',
  xpressgaming: 'xpressgaming',
  sesgaming: 'sesgaming',
  macaw: 'macaw',
  betlotte: 'betlotte',
};

export function walletSessionProvider(providerNavSlug: string): string {
  const key = casinoNavSlug(providerNavSlug);
  return PROVIDER_SESSION_SLUG[key] ?? key;
}

/** Providers that use WalletAPICore JSON session instead of WalletAPI form. */
export const WALLET_CORE_SESSION_PROVIDERS = new Set(['ingames']);

export function isCasinoModuleEnabled(
  modules: { Name?: string | null }[] | null | undefined,
  moduleName: 'casino' | 'livecasino',
): boolean {
  if (!Array.isArray(modules) || modules.length === 0) return true;
  return modules.some(
    (mod) => typeof mod?.Name === 'string' && mod.Name.toLowerCase() === moduleName,
  );
}

export const CASINO_PAGE_SIZE_DESKTOP = 21;
export const CASINO_PAGE_SIZE_MOBILE = 32;
export const CASINO_CAROUSEL_SIZE = 14;

/** Fixed Figma category rail entries (API categories filter visibility). */
export interface CasinoNavItem {
  id: string;
  label: string;
  /** Path under `/casino` or absolute. */
  to: string;
  /** Category name for API filters when browsing a grid. */
  category?: string | null;
  popular?: boolean;
  liveCasino?: boolean;
  /** Require a matching UsersAPI category name (lowercased, no spaces). */
  requireCategory?: string | null;
  favorites?: boolean;
}

export const CASINO_NAV_ITEMS: readonly CasinoNavItem[] = [
  { id: 'lobby', label: 'Lobby', to: '/casino' },
  { id: 'hot', label: 'Hot Games', to: '/casino/hot', popular: true, category: 'slots' },
  { id: 'slots', label: 'Slots', to: '/casino/slots', category: 'slots', requireCategory: 'slots' },
  {
    id: 'livecasino',
    label: 'Live Casino',
    to: '/casino/livecasino',
    category: 'livecasino',
    liveCasino: true,
    requireCategory: 'livecasino',
  },
  {
    id: 'gameshows',
    label: 'Game Shows',
    to: '/casino/gameshows',
    category: 'gameshows',
    requireCategory: 'gameshows',
  },
  {
    id: 'tablegames',
    label: 'Table Games',
    to: '/casino/tablegames',
    category: 'tablegames',
    requireCategory: 'tablegames',
  },
  {
    id: 'blackjack',
    label: 'Blackjack',
    to: '/casino/blackjack',
    category: 'blackjack',
    requireCategory: 'blackjack',
  },
  {
    id: 'roulette',
    label: 'Roulette',
    to: '/casino/roulette',
    category: 'roulette',
    requireCategory: 'roulette',
  },
  {
    id: 'baccarat',
    label: 'Baccarat',
    to: '/casino/baccarat',
    category: 'baccarat',
    requireCategory: 'baccarat',
  },
  { id: 'favorites', label: 'Favorites', to: '/casino/favorites', favorites: true },
];
